import { useState } from "react";
import LegalLayout from "@/components/LegalLayout";
import { ChevronDown, MessageCircle, Mail } from "lucide-react";

const FAQS = [
    {
        q: "How do I use DXF files for CNC cutting?",
        a: "To use DXF files for CNC cutting, you'll need compatible software such as AutoCAD or similar CAD/CAM software. Simply import the DXF file into your software, adjust any settings as needed (such as scale or toolpath), and then send the file to your CNC machine for cutting.",
    },
    {
        q: "What software can I use to open and edit CNC files?",
        a: "There are several software options available for opening and editing CNC files, including AutoCAD, Fusion 360, SolidWorks, ArtCAM, CorelDRAW, and Vectric Aspire etc. Choose the software that best suits your needs and familiarity with CAD/CAM tools.",
    },
    {
        q: "Can I modify the files I purchase from FilesCAD?",
        a: "Yes, you can modify the files purchased from FilesCAD to suit your specific requirements. However, please ensure that you have the necessary rights to modify and use the files, especially if they are subject to copyright or licensing restrictions.",
    },
    {
        q: "How do I troubleshoot issues with CNC file compatibility or cutting errors?",
        a: "If you encounter any issues with CNC file compatibility or cutting errors, please feel free to contact us for assistance. You can reach us via email at support@filescad.com. Provide us with details about the problem you're experiencing, and our team will do our best to help you resolve it.",
    },
    {
        q: "What formats are available for downloading CNC files from FilesCAD?",
        a: "At FilesCAD, we offer a variety of file formats suitable for CNC cutting and engraving, including DXF, STL, SVG, RLF, ART, and more. Choose the format that is compatible with your CNC machine and software, such as AutoCAD, CorelDRAW, RD Works, NC Editor, ArtCAM, Vectric Aspire, and others.",
    },
    {
        q: "Can I request custom designs or file modifications?",
        a: "Yes, we welcome custom design requests and file modifications. Please reach out to us via email at support@filescad.com with your specific requirements, and our team will assist you further.",
    },
    {
        q: "How do I ensure the quality of the CNC files I download from FilesCAD?",
        a: "We strive to provide high-quality CNC files on FilesCAD. However, if you encounter any issues with the quality of the files, please don't hesitate to contact us. We'll address your concerns promptly and ensure that you receive the quality you expect.",
    },
    {
        q: "Are the files compatible with specific CNC machine brands or models?",
        a: "While our files are designed to be compatible with most CNC machines, it's essential to verify compatibility with your specific machine's software and requirements. If you're unsure, feel free to contact us for assistance.",
    },
    {
        q: "Can I use FilesCAD files for commercial purposes?",
        a: "Yes, you can use FilesCAD files for commercial purposes after purchasing them. However, please be mindful of any licensing restrictions or copyright issues associated with the files.",
    },
    {
        q: "Do you offer tutorials or guidance on CNC machining for beginners?",
        a: "Yes, we provide tutorials and guidance for beginners on CNC machining techniques, software usage, and file preparation. Feel free to reach out to us for more information.",
    },
    {
        q: "How can I share feedback or suggestions for FilesCAD?",
        a: "We value your feedback and suggestions. You can share your thoughts with us via email at support@filescad.com or through our social media channels. Your input helps us improve our services and offerings.",
    },
    {
        q: "Are there any restrictions on the usage of FilesCAD files?",
        a: "While there are no specific restrictions on the usage of FilesCAD files, we expect users to adhere to ethical and legal standards when using our content. Please refrain from using the files for any unlawful or unethical purposes.",
    },
    {
        q: "Can I sell the products I create using FilesCAD files?",
        a: "Yes, you can sell products created using FilesCAD files, provided you have the necessary rights to use and modify the files. However, please be aware of any licensing agreements or copyright restrictions associated with the files.",
    },
    {
        q: "Are there any additional benefits to becoming a FilesCAD member?",
        a: "Absolutely. In addition to the unlocked full app, FilesCAD members enjoy benefits such as early access to new releases, priority customer support, and exclusive offers on special events. Join our community of CNC enthusiasts today to unlock these perks.",
    },
    {
        q: "What to do if my purchased file is deleted from the download section?",
        a: "In this case, you have to purchase that particular file again. However, we suggest not deleting any downloaded or purchased files from this section.",
    },
];

export default function FAQ() {
    const [open, setOpen] = useState(0);

    return (
        <LegalLayout testId="faq-page" title="Frequently Asked Questions">
            <p className="text-lg text-slate-700">
                Everything you need to know about CNC files, formats, modifications and membership — answered.
            </p>

            <div className="not-prose mt-10 space-y-3">
                {FAQS.map((f, i) => {
                    const isOpen = open === i;
                    return (
                        <div
                            key={i}
                            data-testid={`faq-page-item-${i}`}
                            className={`border rounded-2xl transition-all ${
                                isOpen ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"
                            }`}
                        >
                            <button
                                data-testid={`faq-page-toggle-${i}`}
                                aria-expanded={isOpen}
                                className="w-full flex items-center justify-between gap-6 p-5 md:p-6 text-left"
                                onClick={() => setOpen(isOpen ? -1 : i)}
                            >
                                <span className="font-display font-bold text-base md:text-lg text-slate-950 leading-snug">
                                    <span className="text-blue-600 font-mono mr-3">{String(i + 1).padStart(2, "0")}</span>
                                    {f.q}
                                </span>
                                <ChevronDown
                                    className={`w-5 h-5 text-slate-500 transition-transform shrink-0 ${
                                        isOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {isOpen && (
                                <div className="px-5 md:px-6 pb-6 text-slate-600 leading-relaxed">{f.a}</div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="not-prose mt-12 rounded-3xl bg-slate-950 text-white p-8 md:p-10">
                <h3 className="font-display font-bold text-2xl">Still have a question?</h3>
                <p className="mt-3 text-slate-400">
                    Reach out and we&rsquo;ll get back to you, usually within 24 hours.
                </p>
                <div className="mt-6 flex flex-wrap gap-4">
                    <a
                        href="mailto:support@filescad.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="faq-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 px-5 py-3 text-sm font-semibold transition"
                    >
                        <MessageCircle className="w-4 h-4" /> support@filescad.com
                    </a>
                    <a
                        href="mailto:support@filescad.com"
                        data-testid="faq-email"
                        className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 hover:bg-slate-100 px-5 py-3 text-sm font-semibold transition"
                    >
                        <Mail className="w-4 h-4" /> support@filescad.com
                    </a>
                </div>
            </div>
        </LegalLayout>
    );
}
